import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts matching pairs from various backend shapes.
 * @param {Object} lesson
 * @returns {Array<{id, left, right}>|null}
 */
function getMatchingPairs(lesson) {
  const raw =
    lesson?.content?.pairs ||
    lesson?.matching_pairs ||
    lesson?.content?.matching_pairs ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((p, i) => ({
      id: p.id || i + 1,
      left: p.left || p.term || p.question || '',
      right: p.right || p.translation || p.answer || '',
    }));
  }
  return null;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * MatchingLesson — 2-column word matching (lesson_type === 'matching').
 *
 * Mode A (default): left col TR, right col EN. Click left → click right → check match.
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.pairs: [{ left, right }]
 *   lesson.matching_pairs: [{ left, right }]
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function MatchingLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawPairs = getMatchingPairs(lesson);
  const hasContent = rawPairs !== null && rawPairs.length > 0;
  // Stabilize: `hasContent ? rawPairs : []` creates a new [] when rawPairs is
  // null, causing buildState useCallback to re-create on every render.
  const basePairs = useMemo(() => hasContent ? rawPairs : [], [hasContent, rawPairs]);

  const buildState = useCallback(() => ({
    leftItems: basePairs.map(p => ({ ...p, side: 'left' })),
    rightItems: shuffle(basePairs.map(p => ({ ...p, side: 'right' }))),
    matched: new Set(),
    mistakes: 0,
    selectedLeft: null,
    selectedRight: null,
    flashId: null,
    flashCorrect: true,
  }), [basePairs]);

  const [state, setState] = useState(buildState);
  const [finished, setFinished] = useState(false);
  const [completing, setCompleting] = useState(false);

  const total = basePairs.length;
  const matchedCount = state.matched.size;

  const selectLeft = (item) => {
    if (state.matched.has(item.id)) return;
    setState(s => ({ ...s, selectedLeft: item.id === s.selectedLeft ? null : item.id }));
  };

  const selectRight = (item) => {
    if (state.matched.has(item.id)) return;
    if (!state.selectedLeft) return;

    const isMatch = state.selectedLeft === item.id;

    if (isMatch) {
      const newMatched = new Set([...state.matched, item.id]);
      setState(s => ({
        ...s,
        matched: newMatched,
        selectedLeft: null,
        selectedRight: null,
        flashId: item.id,
        flashCorrect: true,
      }));
      setTimeout(() => setState(s => ({ ...s, flashId: null })), 800);
      if (newMatched.size >= total) {
        setTimeout(() => setFinished(true), 900);
      }
    } else {
      setState(s => ({
        ...s,
        mistakes: s.mistakes + 1,
        selectedLeft: null,
        selectedRight: null,
        flashId: item.id,
        flashCorrect: false,
      }));
      setTimeout(() => setState(s => ({ ...s, flashId: null })), 600);
    }
  };

  const restart = useCallback(() => {
    setState(buildState());
    setFinished(false);
  }, [buildState]);

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const score = state.mistakes === 0 ? 100 : Math.max(0, Math.round(100 - (state.mistakes / total) * 50));
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="matching-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="matching-empty-state">
        <AlertCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz eşleştirme içeriği tanımlanmamış.' : 'No matching content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="matching-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="matching-lesson">
      {/* Finished screen */}
      <AnimatePresence>
        {finished && (
          <motion.div
            initial={reduced ? {} : { opacity: 0, scale: 0.9 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-8 text-center"
            data-testid="matching-completed"
          >
            <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-emerald-700 mb-1">
              {t('matchingLesson.completed')}
            </h2>
            <p className="text-gray-600 font-semibold mb-2">
              {total}/{total} {t('matchingLesson.matched')}
            </p>
            <p className="text-sm text-rose-500 font-semibold mb-6">
              {t('matchingLesson.mistakes')}: {state.mistakes}
            </p>
            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="matching-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={restart}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="matching-restart-btn"
              >
                <RefreshCw size={16} /> {t('matchingLesson.restart')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!finished && (
        <>
          {/* Progress */}
          <div className="flex items-center justify-between mb-4 text-sm font-bold">
            <span className="text-gray-500">
              {matchedCount}/{total} {t('matchingLesson.matched')}
            </span>
            {state.mistakes > 0 && (
              <span className="flex items-center gap-1 text-rose-500">
                <AlertCircle size={14} />
                {t('matchingLesson.mistakes')}: {state.mistakes}
              </span>
            )}
          </div>

          {/* Hint text */}
          <p className="text-xs text-center text-gray-400 font-semibold mb-4">
            {state.selectedLeft === null ? t('matchingLesson.selectLeft') : t('matchingLesson.selectRight')}
          </p>

          {/* Two-column grid */}
          <div className="grid grid-cols-2 gap-3" data-testid="matching-grid">
            {/* Left column */}
            <div className="space-y-2" data-testid="matching-left-col">
              {state.leftItems.map(item => {
                const isMatchedItem = state.matched.has(item.id);
                const isSelected = state.selectedLeft === item.id;
                const isFlashing = state.flashId === item.id;

                let cls = 'w-full px-4 py-3 rounded-xl border-2 font-bold text-sm text-left transition-all cursor-pointer ';
                if (isMatchedItem) {
                  cls += 'border-emerald-400 bg-emerald-50 text-emerald-700 cursor-default opacity-60';
                } else if (isFlashing && !state.flashCorrect) {
                  cls += 'border-rose-400 bg-rose-50 text-rose-700';
                } else if (isFlashing && state.flashCorrect) {
                  cls += 'border-emerald-400 bg-emerald-100 text-emerald-700';
                } else if (isSelected) {
                  cls += 'border-cyan-500 bg-cyan-50 text-cyan-700 shadow-[0_3px_0_#06b6d4]';
                } else {
                  cls += 'border-gray-200 bg-white text-gray-700 shadow-[0_3px_0_#d1d5db] hover:border-cyan-300 hover:bg-cyan-50';
                }

                return (
                  <motion.button
                    key={item.id}
                    whileTap={reduced ? {} : (isMatchedItem ? {} : { scale: 0.97 })}
                    onClick={() => !isMatchedItem && selectLeft(item)}
                    className={cls}
                    disabled={isMatchedItem}
                    aria-label={item.left}
                    aria-pressed={isSelected}
                    data-testid={`matching-left-${item.id}`}
                  >
                    {item.left}
                    {isMatchedItem && (
                      <CheckCircle size={14} className="inline ml-2 text-emerald-500" />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Right column */}
            <div className="space-y-2" data-testid="matching-right-col">
              {state.rightItems.map(item => {
                const isMatchedItem = state.matched.has(item.id);
                const isFlashing = state.flashId === item.id;

                let cls = 'w-full px-4 py-3 rounded-xl border-2 font-bold text-sm text-left transition-all cursor-pointer ';
                if (isMatchedItem) {
                  cls += 'border-emerald-400 bg-emerald-50 text-emerald-700 cursor-default opacity-60';
                } else if (isFlashing && !state.flashCorrect) {
                  cls += 'border-rose-400 bg-rose-50 text-rose-700 animate-pulse';
                } else if (isFlashing && state.flashCorrect) {
                  cls += 'border-emerald-400 bg-emerald-100 text-emerald-700';
                } else {
                  cls += 'border-gray-200 bg-white text-gray-700 shadow-[0_3px_0_#d1d5db] hover:border-cyan-300 hover:bg-cyan-50';
                }

                return (
                  <motion.button
                    key={item.id}
                    whileTap={reduced ? {} : (isMatchedItem ? {} : { scale: 0.97 })}
                    onClick={() => !isMatchedItem && selectRight(item)}
                    className={cls}
                    disabled={isMatchedItem}
                    aria-label={item.right}
                    data-testid={`matching-right-${item.id}`}
                  >
                    {item.right}
                    {isMatchedItem && (
                      <CheckCircle size={14} className="inline ml-2 text-emerald-500" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
